import React from 'react';
import { ethers } from 'ethers';
import logger from '../../utils/logger';

// Smart Template interface that extends the existing Template structure
export interface SmartTemplate {
    // Core template data (compatible with existing system)
    contract: string;
    functionName: string;
    inputs: string[];

    // Enhanced metadata for smart templates
    category: 'fee-management' | 'governance' | 'treasury' | 'liquidity' | 'general';
    action: string;
    description: string;
    contractAddress?: string;

    // Calculated parameters
    estimatedGas?: string;
    gasPrice?: string;
    profitability?: {
        feesValue: string;
        gasCost: string;
        netProfit: string;
        isViable: boolean;
    };

    // User-provided inputs that need to be filled
    userInputs?: {
        [parameterName: string]: {
            type: string;
            description: string;
            required: boolean;
            defaultValue?: string;
        };
    };

    // Metadata
    createdAt: number;
    createdBy?: string;
    tags?: string[];
}

// Fee Management Template Generators
export class FeeManagementTemplates {

    /**
     * Generate template for collecting all fees from Uniswap positions
     */
    static generateCollectAllFeesTemplate(
        contractAddress: string,
        estimatedGas?: string,
        feeMetrics?: {
            unclaimedFeesETH: string;
            estimatedGasCost: string;
            profitAfterGas: string;
            isCollectionViable: boolean;
        }
    ): SmartTemplate {
        return {
            contract: 'UniswapLiquiditySetup',
            contractAddress,
            functionName: 'collectAllFees',
            inputs: [], // No parameters needed for collectAllFees
            category: 'fee-management',
            action: 'collect-all-fees',
            description: 'Collect all unclaimed fees from Uniswap V3 positions',
            estimatedGas,
            profitability: feeMetrics ? {
                feesValue: feeMetrics.unclaimedFeesETH,
                gasCost: feeMetrics.estimatedGasCost,
                netProfit: feeMetrics.profitAfterGas,
                isViable: feeMetrics.isCollectionViable
            } : undefined,
            createdAt: Date.now(),
            tags: ['uniswap', 'fees', 'collection']
        };
    }

    /**
     * Generate template for collecting and reinvesting fees
     */
    static generateCollectAndReinvestTemplate(
        contractAddress: string,
        tokenId: string,
        minToken0?: string,
        minToken1?: string,
        estimatedGas?: string
    ): SmartTemplate {
        const inputs: string[] = [tokenId];

        // Add slippage parameters if provided
        if (minToken0 !== undefined) inputs.push(minToken0);
        if (minToken1 !== undefined) inputs.push(minToken1);

        return {
            contract: 'UniswapLiquiditySetup',
            contractAddress,
            functionName: 'collectAndReinvestFees',
            inputs,
            category: 'fee-management',
            action: 'collect-and-reinvest',
            description: `Collect fees from position #${tokenId} and reinvest into liquidity`,
            estimatedGas,
            userInputs: minToken0 === undefined ? {
                'minToken0': {
                    type: 'uint256',
                    description: 'Minimum amount of token0 to receive (slippage protection)',
                    required: false,
                    defaultValue: '0'
                },
                'minToken1': {
                    type: 'uint256',
                    description: 'Minimum amount of token1 to receive (slippage protection)',
                    required: false,
                    defaultValue: '0'
                }
            } : undefined,
            createdAt: Date.now(),
            tags: ['uniswap', 'fees', 'reinvest', 'compound']
        };
    }

    /**
     * Generate template for enabling auto-compounding
     */
    static generateEnableAutoCompoundTemplate(
        contractAddress: string,
        tokenId: string,
        minFeeThreshold: string,
        gasMultiplier: string,
        minInterval: string
    ): SmartTemplate {
        return {
            contract: 'UniswapLiquiditySetup',
            contractAddress,
            functionName: 'enableAutoCompounding',
            inputs: [
                tokenId,
                ethers.utils.parseEther(minFeeThreshold).toString(),
                gasMultiplier,
                (parseInt(minInterval) * 3600).toString() // Convert hours to seconds
            ],
            category: 'fee-management',
            action: 'enable-auto-compound',
            description: `Enable auto-compounding for position #${tokenId}`,
            userInputs: {
                'minFeeThreshold': {
                    type: 'uint256',
                    description: 'Minimum fee threshold in ETH to trigger compounding',
                    required: true,
                    defaultValue: minFeeThreshold
                },
                'gasMultiplier': {
                    type: 'uint256',
                    description: 'Fees must be X times gas cost (recommended: 3)',
                    required: true,
                    defaultValue: gasMultiplier
                },
                'minInterval': {
                    type: 'uint256',
                    description: 'Minimum interval between compounds (in hours)',
                    required: true,
                    defaultValue: minInterval
                }
            },
            createdAt: Date.now(),
            tags: ['uniswap', 'auto-compound', 'automation']
        };
    }
}

// Aerodrome Template Generators
export class AerodromeTemplates {

    /**
     * Generate template for collecting Aerodrome pool fees
     */
    static generateCollectPoolFeesTemplate(
        contractAddress: string,
        poolAddress: string,
        estimatedGas?: string
    ): SmartTemplate {
        return {
            contract: 'AerodromeLiquiditySetup',
            contractAddress,
            functionName: 'collectPoolFees',
            inputs: [poolAddress],
            category: 'fee-management',
            action: 'collect-pool-fees',
            description: `Collect fees from Aerodrome pool ${poolAddress.slice(0, 10)}...`,
            estimatedGas,
            createdAt: Date.now(),
            tags: ['aerodrome', 'fees', 'collection']
        };
    }

    /**
     * Generate template for collecting and reinvesting Aerodrome fees
     */
    static generateCollectAndReinvestPoolTemplate(
        contractAddress: string,
        poolAddress: string,
        min0: string,
        min1: string,
        estimatedGas?: string
    ): SmartTemplate {
        return {
            contract: 'AerodromeLiquiditySetup',
            contractAddress,
            functionName: 'collectAndReinvestFees',
            inputs: [poolAddress, min0, min1],
            category: 'fee-management',
            action: 'collect-and-reinvest-pool',
            description: `Collect and reinvest fees from Aerodrome pool`,
            estimatedGas,
            userInputs: {
                'min0': {
                    type: 'uint256',
                    description: 'Minimum amount of token0 to receive',
                    required: false,
                    defaultValue: min0
                },
                'min1': {
                    type: 'uint256',
                    description: 'Minimum amount of token1 to receive',
                    required: false,
                    defaultValue: min1
                }
            },
            createdAt: Date.now(),
            tags: ['aerodrome', 'fees', 'reinvest', 'compound']
        };
    }
}

// Governance Template Generators
export class GovernanceTemplates {

    /**
     * Generate template for creating a governance proposal
     */
    static generateCreateProposalTemplate(
        contractAddress: string,
        targets: string[],
        values: string[],
        calldatas: string[],
        description: string
    ): SmartTemplate {
        return {
            contract: 'MemerCharityDAOGovernanceSimplified',
            contractAddress,
            functionName: 'propose',
            inputs: [
                JSON.stringify(targets),
                JSON.stringify(values),
                JSON.stringify(calldatas),
                description
            ],
            category: 'governance',
            action: 'create-proposal',
            description: `Create governance proposal: ${description.slice(0, 50)}...`,
            userInputs: {
                'targets': {
                    type: 'address[]',
                    description: 'Target contract addresses for proposal actions',
                    required: true
                },
                'values': {
                    type: 'uint256[]',
                    description: 'ETH values to send with each action',
                    required: true
                },
                'calldatas': {
                    type: 'bytes[]',
                    description: 'Encoded function calls for each action',
                    required: true
                },
                'description': {
                    type: 'string',
                    description: 'Proposal description',
                    required: true,
                    defaultValue: description
                }
            },
            createdAt: Date.now(),
            tags: ['governance', 'proposal', 'dao']
        };
    }
}

// Treasury Template Generators  
export class TreasuryTemplates {

    /**
     * Generate template for MEMER token transfer
     */
    static generateTokenTransferTemplate(
        contractAddress: string,
        to: string,
        amount: string,
        description?: string
    ): SmartTemplate {
        return {
            contract: 'MemerCore',
            contractAddress,
            functionName: 'transfer',
            inputs: [to, ethers.utils.parseEther(amount).toString()],
            category: 'treasury',
            action: 'token-transfer',
            description: description || `Transfer ${amount} MEMER to ${to.slice(0, 10)}...`,
            userInputs: {
                'to': {
                    type: 'address',
                    description: 'Recipient address',
                    required: true,
                    defaultValue: to
                },
                'amount': {
                    type: 'uint256',
                    description: 'Amount of MEMER tokens to transfer',
                    required: true,
                    defaultValue: amount
                }
            },
            createdAt: Date.now(),
            tags: ['treasury', 'transfer', 'memer']
        };
    }
}

// Main Smart Template Builder Class
export class SmartTemplateBuilder {

    /**
     * Create a smart template and save it to localStorage
     */
    static createAndSaveTemplate(template: SmartTemplate): void {
        try {
            // Get existing templates
            const existingTemplates = this.loadTemplates();

            // Add new template
            existingTemplates.push(template);

            // Save back to localStorage
            localStorage.setItem('smartTransactionTemplates', JSON.stringify(existingTemplates));

            logger.log('Smart template created and saved:', template);
        } catch (error) {
            logger.error('Error saving smart template:', error);
            throw error;
        }
    }

    /**
     * Load all smart templates from localStorage
     */
    static loadTemplates(): SmartTemplate[] {
        try {
            const saved = localStorage.getItem('smartTransactionTemplates');
            return saved ? JSON.parse(saved) : [];
        } catch (error) {
            logger.error('Error loading smart templates:', error);
            return [];
        }
    }

    /**
     * Get templates by category
     */
    static getTemplatesByCategory(category: SmartTemplate['category']): SmartTemplate[] {
        return this.loadTemplates().filter(template => template.category === category);
    }

    /**
     * Get templates by contract
     */
    static getTemplatesByContract(contractName: string): SmartTemplate[] {
        return this.loadTemplates().filter(template => template.contract === contractName);
    }

    /**
     * Convert smart template to legacy template format for FunctionBuilder
     */
    static convertToLegacyTemplate(smartTemplate: SmartTemplate): {
        contract: string;
        functionName: string;
        inputs: string[];
    } {
        return {
            contract: smartTemplate.contract,
            functionName: smartTemplate.functionName,
            inputs: smartTemplate.inputs
        };
    }

    /**
     * Generate template for launching FunctionBuilder with pre-filled data
     */
    static generateFunctionBuilderLaunchData(template: SmartTemplate): {
        contractName: string;
        functionName: string;
        inputs: { [key: string]: string };
        template: { contract: string; functionName: string; inputs: string[] };
    } {
        // Convert inputs array to object keyed by parameter names
        const inputsObject: { [key: string]: string } = {};

        // This would need to be enhanced with actual ABI parsing to get parameter names
        // For now, using generic parameter names
        template.inputs.forEach((input, index) => {
            inputsObject[`param${index}`] = input;
        });

        return {
            contractName: template.contract,
            functionName: template.functionName,
            inputs: inputsObject,
            template: this.convertToLegacyTemplate(template)
        };
    }

    /**
     * Delete a template
     */
    static deleteTemplate(templateIndex: number): void {
        try {
            const templates = this.loadTemplates();
            templates.splice(templateIndex, 1);
            localStorage.setItem('smartTransactionTemplates', JSON.stringify(templates));
            logger.log('Template deleted at index:', templateIndex);
        } catch (error) {
            logger.error('Error deleting template:', error);
            throw error;
        }
    }

    /**
     * Clear all templates
     */
    static clearAllTemplates(): void {
        localStorage.removeItem('smartTransactionTemplates');
        logger.log('All smart templates cleared');
    }
}

export default SmartTemplateBuilder;
